import { type Maybe, type ValueEvent } from '@adornis/base/utilTypes.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { book } from '@adornis/buchungsheld-db/api/booking.js';
import { Category } from '@adornis/buchungsheld-db/entities/category.js';
import { Institution } from '@adornis/buchungsheld-db/entities/institution.js';
import { Participation } from '@adornis/buchungsheld-db/entities/Participation.js';
import type { Product } from '@adornis/buchungsheld-db/entities/products.js';
import {
  RegistrationData,
  RegistrationWorkshopData,
} from '@adornis/buchungsheld-db/entities/RegistrationDataBuffer.js';
import { UserStammdaten } from '@adornis/buchungsheld-db/entities/stammdaten.js';
import { Workshop } from '@adornis/buchungsheld-db/entities/workshop.js';
import { ParticipationState } from '@adornis/buchungsheld-db/enums/enums.js';
import { formatPrice } from '@adornis/buchungsheld-db/global-helpers.js';
import type { PaymentPlugin } from '@adornis/buchungsheld-db/plugins/plugin-types.js';
import { getPluginForScope } from '@adornis/buchungsheld-db/plugins/plugin.js';
import { TrDialog } from '@adornis/buchungsheld-theme/components/tr-dialog.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { acss } from '@adornis/chemistry/directives/acss.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import { XDialog } from '@adornis/dialog/x-dialog.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import type { AdornisUser } from '@adornis/users/db/a-user.js';
import { html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { until } from 'lit/directives/until.js';
import { combineLatest, filter, map, switchMap } from 'rxjs';

@customElement('bh-prompt-finalize-payment')
export class BHPromptFinalizePayment extends TrDialog<Maybe<Category>> {
  @property() participationID = new RXController<string>(this, undefined);

  @state() participation = new RXController(
    this,
    this.participationID.observable.pipe(
      filter(Boolean),
      switchMap(participationID =>
        getByID(
          Participation,
          participationID,
        )({ _id: 1, state: 1, userID: 1, productID: 1, product: { _id: 1, institutionID: 1 } }),
      ),
      filter(Boolean),
    ),
  );

  @state() institutionID = new RXController(
    this,
    this.participation.observable.pipe(
      filter(Boolean),
      map(participation => participation.product!.institutionID),
      filter(Boolean),
    ),
  );

  @state() institution = new RXController(
    this,
    this.institutionID.observable.pipe(
      filter(Boolean),
      switchMap(institutionID => getByID(Institution, institutionID)({ _id: 1 })),
    ),
  );

  @state() stammdaten = new RXController(
    this,
    combineLatest([
      this.participation.observable.pipe(filter(Boolean)),
      this.institutionID.observable.pipe(filter(Boolean)),
    ]).pipe(
      switchMap(([participation, institutionID]) =>
        UserStammdaten.getExistingStammdaten(participation.userID, institutionID, null)({}),
      ),
    ),
  );

  @state() user = new RXController(this, currentUser());

  static override get element_name() {
    return 'bh-prompt-finalize-payment';
  }

  @state() private readonly paymentMethods = new RXController(
    this,
    this.participation.observable.pipe(
      map(participation => {
        const institutionID = participation?.product?.institutionID;
        if (!institutionID) throw new Error('institutionID not found');
        const paymentPlugin = getPluginForScope('payment', institutionID);
        return paymentPlugin.paymentMethods;
      }),
    ),
  );

  private paymentMethod = '';
  protected _renderProduct(
    curProd: Product,
    curUser: AdornisUser,
    payment: PaymentPlugin<string, string>,
    inBundle = false,
  ) {
    const price = (async () => {
      if (curProd instanceof Workshop && curProd.leftCapacity === 0) return 0; // waitingList products are paid when claiming a spot
      const price = await payment.getProductPrice(curProd._id, curUser._id);
      if (!price || !price.unitAmount) return null;
      return formatPrice(price.unitAmount);
    })();

    return [
      html`
        <tr-text ?bold=${!inBundle} ${css(inBundle ? { paddingLeft: this.spacing.xs } : {})}> ${curProd.name} </tr-text>
        <tr-text ${css(inBundle ? { textDecoration: 'line-through' } : {})}> ${until(price, '0,00 €')}</tr-text>
      `,
    ];
  }

  override render() {
    const product = this.participation.value?.product;
    const paymentMethods = this.paymentMethods.value;
    const user = this.user.value;
    if (!this.institution.value || !paymentMethods || !product || !user)
      return html`<x-loading-state></x-loading-state>`;
    if (!this.paymentMethod) this.paymentMethod = paymentMethods[0] ?? '';

    if (paymentMethods.length === 0) return html`no paymentMethods found`;

    const stammdaten = this.stammdaten.value;
    const paymentPlugin = getPluginForScope('payment', this.institution.value._id);
    const price = paymentPlugin.getProductPrice(product._id, user._id);
    const priceStr = price.then(price => {
      return formatPrice(price?.unitAmount ?? 0, '0,00 €');
    });

    if (!user || !stammdaten) return html`<x-loading-state></x-loading-state>`;

    return html`
      <x-flex vertical space="md">
        <x-h3> ${this._translation.translate('PAYMENT', 'payment')} </x-h3>
        <tr-text> ${this._translation.translate('PAYMENT', 'select_method')} </tr-text>

        <x-dropdown-selection
          .selectables=${this.paymentMethods.value}
          placeholder=${this._translation.translate('PAYMENT', 'method')}
          .value=${this.paymentMethod}
          .renderString=${item => this._translation.translate('PAYMENT', 'methods', item)}
          @value-picked=${(e: ValueEvent<any>) => {
            this.paymentMethod = e.detail.value;
          }}
        ></x-dropdown-selection>

        <!-- price preview -->
        <tr-flex
          crossaxis-end
          ${acss({
            padding: `0 ${this.spacing.lg}`,
            margin: '30px 0',

            'tr-grid': {
              gap: `${this.spacing.sm} ${this.spacing.md}`,

              'tr-text': { textWrap: 'nowrap' },
              '[total]': {
                display: 'contents',

                'tr-icon': {
                  justifySelf: 'flex-end',
                  alignSelf: 'center',
                },
                'tr-text': {
                  borderTop: `2px solid ${this.colors.dark}`,
                  padding: `${this.spacing.xs} 0`,
                },
              },
            },
          })}
        >
          <tr-grid vertical columns="min-content min-content">
            ${this._renderProduct(product, user, paymentPlugin)}

            <tr-flex total>
              <tr-icon>point_of_sale</tr-icon>
              <tr-text>${until(priceStr, html`<x-loader></x-loader>`)}</tr-text>
            </tr-flex>
          </tr-grid>
        </tr-flex>

        <x-button
          @click=${async () => {
            try {
              const participation = this.participation.value;
              if (!participation) throw new Error('something went wrong');

              const getBookingResult = async () => {
                // waitnig and nachruecken
                if (participation.state === ParticipationState.WAITING) {
                  return await RegistrationData.moveUpFromWaitinglistIntoParticipating(
                    participation._id,
                    this.paymentMethod,
                  )({ paymentRequired: 1, paymentIDs: 1 });
                }

                // reregister
                if (participation.state === ParticipationState.LEFT) {
                  const productData = (() => {
                    return [new RegistrationWorkshopData({ productID: participation.productID })];
                  })();

                  return await book(productData, this.paymentMethod)({ paymentRequired: 1, paymentIDs: 1 });
                }

                throw new Error('not implemented');
              };

              await XDialog.waitFor(
                (async () => {
                  const bookingResult = await getBookingResult();
                  if (!bookingResult) throw new Error('bookingResult is undefined');

                  if (bookingResult.paymentRequired) {
                    if (!bookingResult.paymentIDs) throw new Error('payment is required but paymentIDs are undiefined');
                    for (const paymentID of bookingResult.paymentIDs)
                      await paymentPlugin.pay(paymentID, this.paymentMethod);
                  }
                })(),
              );
              XSnackbar.show(this._translation.translate('actions', 'that_worked'));
              this.close();
            } catch (err) {
              console.log(err);
              XSnackbar.show(this._translation.translate('err', 'something_went_wrong'));
            }
          }}
          >${this._translation.translate('REGISTRATION', 'book_now')}</x-button
        >
      </x-flex>
    `;
  }
}
